import type { NodeKind } from '@netdeck/contracts';
import type { Status } from '../../shared/ui/StatusDot';

/** Точка в координатах демо-схемы. */
export interface Point {
  x: number;
  y: number;
}

/**
 * Холст демо-схемы.
 *
 * Схема живёт в собственных координатах и масштабируется под панель целиком:
 * расстояния между карточками остаются такими, как нарисованы, на любой
 * ширине окна, и раскладку не приходится пересчитывать под каждый размер.
 */
export const DEMO_SCENE = { width: 480, height: 460 } as const;

/**
 * Размер карточки узла.
 *
 * Высота задана жёстко, а не отдана содержимому: по ней считаются точки,
 * из которых выходят линии связей. Посчитанная по-другому высота увела бы
 * линии от края карточки, и промах был бы виден на схеме сразу.
 */
export const DEMO_NODE = { width: 148, height: 58 } as const;

/** Поля рамки: за них карточку не вытянуть. */
const PADDING = 6;

/** Высота заголовка рамки — под него карточку тоже не подсунуть. */
const HEADER = 26;

export interface DemoNode {
  id: string;
  name: string;
  address: string;
  kind: NodeKind;
  status: Status;
  /** Короткая сводка по мониторам — как на карточке основной карты. */
  summary: string;
  /** Начальное положение левого верхнего угла карточки. */
  at: Point;
}

export interface DemoLink {
  from: string;
  to: string;
  label?: string;
}

/**
 * Демонстрационная площадка.
 *
 * Придуманная, но правдоподобная: шлюз, коммутатор и три объекта за ним,
 * все четыре состояния сразу. Адреса из 10.20.0.0/24 — той же сети, что
 * в примере из README, чтобы схема на входе и схема в документации
 * читались как одно и то же место.
 *
 * Узлов ровно пять, и типы у всех разные. Схема на входе не опись парка,
 * а обещание: за десять секунд, что на неё смотрят, читается ряд карточек,
 * а не пересчитывается их количество, — и каждая лишняя отнимает у остальных
 * место и внимание.
 *
 * Раскладка — две колонки. Слева вертикаль «шлюз — коммутатор — сервер»,
 * справа, вразбежку по высоте, объекты за коммутатором: кривые к ним
 * расходятся веером через пустой промежуток между колонками и не проходят
 * ни под одной карточкой.
 */
export const DEMO_NODES: readonly DemoNode[] = [
  {
    id: 'gw',
    name: 'auth.demo.node.gateway',
    address: '10.20.0.1',
    kind: 'router',
    status: 'up',
    summary: 'auth.demo.hint.icmp2ms',
    at: { x: 44, y: 65 },
  },
  {
    id: 'sw',
    name: 'auth.demo.node.core',
    address: '10.20.0.2',
    kind: 'switch',
    status: 'up',
    summary: 'auth.demo.hint.snmp18',
    at: { x: 44, y: 205 },
  },
  {
    id: 'srv',
    name: 'auth.demo.node.server',
    address: '10.20.0.20',
    kind: 'server',
    status: 'unknown',
    summary: 'auth.demo.hint.noChecks',
    at: { x: 44, y: 343 },
  },
  {
    id: 'pc',
    name: 'auth.demo.node.pos',
    address: '10.20.0.11',
    kind: 'pc',
    status: 'warn',
    summary: 'auth.demo.hint.tcpSilent',
    at: { x: 288, y: 127 },
  },
  {
    id: 'cam',
    name: 'auth.demo.node.camera',
    address: '10.20.0.50',
    kind: 'camera',
    status: 'down',
    summary: 'auth.demo.hint.icmpDown',
    at: { x: 288, y: 294 },
  },
];

export const DEMO_LINKS: readonly DemoLink[] = [
  { from: 'gw', to: 'sw', label: 'uplink' },
  { from: 'sw', to: 'srv' },
  { from: 'sw', to: 'pc' },
  { from: 'sw', to: 'cam' },
];

/**
 * Доля ширины правой зоны, которую занимает схема.
 *
 * Схема — иллюстрация, а не содержимое экрана: упёртая в края панели, она
 * читается как обрезанная, и поля вокруг неё нужны ровно затем, чтобы было
 * видно, что показали её целиком.
 */
const WIDTH_RATIO = 0.8;

/**
 * Наименьший масштаб. Мельче подписи на карточках уже не прочесть, и лучше
 * подрезать схему по краю панели, чем показать нечитаемую целиком.
 */
const MIN_SCALE = 0.6;

/**
 * Масштаб схемы под размер правой зоны.
 *
 * Ведёт ширина: схема занимает свою долю зоны и растёт вместе с ней, а не
 * замирает на нарисованном размере посреди широкой панели. Высота только
 * ограничивает — на низком окне схема ужимается, чтобы не вылезти за панель,
 * иначе нижний ряд карточек уходил бы под подпись.
 */
export function sceneScale(width: number, height: number): number {
  return Math.max(
    MIN_SCALE,
    Math.min((width * WIDTH_RATIO) / DEMO_SCENE.width, height / DEMO_SCENE.height),
  );
}

/**
 * Держит карточку внутри рамки.
 *
 * Уехавшая за край карточка на схеме входа выглядит поломкой, а не свободой:
 * ограничение проще, чем прокрутка холста, и ничего не отнимает — двигать
 * объекты всё равно можно.
 */
export function clampToScene(at: Point): Point {
  return {
    x: clamp(at.x, PADDING, DEMO_SCENE.width - DEMO_NODE.width - PADDING),
    y: clamp(at.y, HEADER + PADDING, DEMO_SCENE.height - DEMO_NODE.height - PADDING),
  };
}

export interface LinkGeometry {
  path: string;
  /** Кривая, разбитая на отрезки: по ней проверяется раскладка. */
  points: Point[];
  /** Середина линии — место для подписи. */
  label: Point;
}

/** Сколько отрезков заменяют кривую при проверке раскладки. */
const CURVE_SAMPLES = 24;

/**
 * Линия между двумя карточками: плавной кривой.
 *
 * Кривая Безье выходит из карточки перпендикулярно её краю и так же входит
 * в соседнюю — поэтому контрольные точки лежат на одной высоте (или вертикали)
 * с концами. Ступенька на схеме входа читалась чертежом; кривая при
 * перетаскивании тянется за карточкой, а не перещёлкивает углы.
 *
 * Сторона выхода выбирается по взаимному положению карточек, а не назначается
 * заранее: объекты двигают мышью, и линия, выходящая из левого бока к соседу
 * справа, сразу выдала бы схему нарисованной, а не живой.
 */
export function linkGeometry(from: Point, to: Point): LinkGeometry {
  const a = center(from);
  const b = center(to);
  const horizontal = Math.abs(b.x - a.x) > Math.abs(b.y - a.y);

  const start = horizontal
    ? { x: b.x > a.x ? from.x + DEMO_NODE.width : from.x, y: a.y }
    : { x: a.x, y: b.y > a.y ? from.y + DEMO_NODE.height : from.y };

  const end = horizontal
    ? { x: b.x > a.x ? to.x : to.x + DEMO_NODE.width, y: b.y }
    : { x: b.x, y: b.y > a.y ? to.y : to.y + DEMO_NODE.height };

  const middle = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };

  const first = horizontal ? { x: middle.x, y: start.y } : { x: start.x, y: middle.y };
  const second = horizontal ? { x: middle.x, y: end.y } : { x: end.x, y: middle.y };

  const points = Array.from({ length: CURVE_SAMPLES + 1 }, (_, index) =>
    bezier(start, first, second, end, index / CURVE_SAMPLES),
  );

  return {
    path:
      `M ${round(start.x)} ${round(start.y)}` +
      ` C ${round(first.x)} ${round(first.y)} ${round(second.x)} ${round(second.y)}` +
      ` ${round(end.x)} ${round(end.y)}`,
    points,
    // Контрольные точки симметричны относительно середины, и кривая проходит
    // ровно через неё: подпись встаёт на линию без вычисления по кривой.
    label: middle,
  };
}

/** Точка кубической кривой Безье при параметре `t` от 0 до 1. */
function bezier(p0: Point, p1: Point, p2: Point, p3: Point, t: number): Point {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;

  return {
    x: a * p0.x + b * p1.x + c * p2.x + d * p3.x,
    y: a * p0.y + b * p1.y + c * p2.y + d * p3.y,
  };
}

function center(at: Point): Point {
  return { x: at.x + DEMO_NODE.width / 2, y: at.y + DEMO_NODE.height / 2 };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Два знака после запятой: путь читаемый, а разницы на экране нет. */
function round(value: number): number {
  return Math.round(value * 100) / 100;
}
